import { useMutation } from '@ngtrvu/use-api';
import { Button, Form, Input, message, Modal, Space, Tag } from 'antd';
import BooleanIcons from 'common/ui/boolean-icons';
import InfoCard from 'common/ui/info-card';
import { useState } from 'react';
import { formatDateTime } from 'utils/formatDate';
import { STAFF_PASSWORD_HINT, staffPasswordRule } from 'utils/password-policy';

const roleColors: Record<string, string> = {
  superadmin: 'red',
  admin: 'blue',
  staff: 'green',
};

const ResetPasswordModal = ({
  open,
  onClose,
  staffAccountId,
  email,
  apiCall,
}: {
  open: boolean;
  onClose: () => void;
  staffAccountId: string | number;
  email?: string;
  apiCall: any;
}) => {
  const [form] = Form.useForm();

  const { mutate, isPending } = useMutation(apiCall.resetPassword, {
    onSuccess: () => {
      message.success('Password has been reset');
      form.resetFields();
      onClose();
    },
    onError: (err: any) => {
      message.error(err?.message || 'Failed to reset password');
    },
  });

  return (
    <Modal
      title="Reset Password"
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={({ password }) => {
          mutate({ id: String(staffAccountId), payload: { password } });
        }}
      >
        <Form.Item
          name="password"
          label="New Password"
          extra={STAFF_PASSWORD_HINT}
          rules={[
            { required: true, message: 'Please enter a new password' },
            staffPasswordRule(() => email),
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          name="confirm_password"
          label="Confirm Password"
          dependencies={['password']}
          rules={[
            { required: true, message: 'Please confirm the password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords do not match'));
              },
            }),
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Form.Item className="mb-0">
          <Space className="w-full justify-end">
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={isPending}>
              Reset Password
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

// Turns off 2FA for a staff member who lost their authenticator device; they
// set it up again at their next sign-in (required for admin/superadmin).
const ResetTwoFactorButton = ({
  staffAccountId,
  email,
  apiCall,
  onDone,
}: {
  staffAccountId: string | number;
  email: string;
  apiCall: any;
  onDone?: () => void;
}) => {
  const { mutate, isPending } = useMutation(apiCall.resetTwoFactor, {
    onSuccess: () => {
      message.success('Đã đặt lại xác thực hai lớp');
      onDone?.();
    },
    onError: (err: any) => {
      message.error(err?.message || 'Không đặt lại được xác thực hai lớp');
    },
  });

  return (
    <Button
      danger
      loading={isPending}
      onClick={() =>
        Modal.confirm({
          title: 'Reset 2FA?',
          content: `Tắt xác thực hai lớp và xóa các mã dự phòng của ${email}. Chỉ làm khi người dùng mất thiết bị xác thực và đã được xác minh danh tính.`,
          okText: 'Reset 2FA',
          okButtonProps: { danger: true },
          cancelText: 'Hủy',
          onOk: () => mutate({ id: String(staffAccountId) }),
        })
      }
    >
      Reset 2FA
    </Button>
  );
};

const ItemDetail = ({
  item,
  apiCall,
  onRefresh,
}: {
  item: any;
  apiCall: any;
  onRefresh?: () => void;
}) => {
  const [resetOpen, setResetOpen] = useState(false);

  const {
    id,
    name,
    title,
    email,
    role,
    is_active,
    has_password,
    two_factor_enabled,
    created_at,
    updated_at,
  } = item || {};

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        {two_factor_enabled && (
          <ResetTwoFactorButton
            staffAccountId={id}
            email={email}
            apiCall={apiCall}
            onDone={onRefresh}
          />
        )}
        <Button type="primary" onClick={() => setResetOpen(true)}>
          Reset Password
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <InfoCard label="ID" value={id} />
        <InfoCard label="Email" value={email} />
        <InfoCard label="Name" value={name || '-'} />
        <InfoCard label="Title" value={title || '-'} />
        <InfoCard
          label="Role"
          value={
            role ? <Tag color={roleColors[role] || 'default'}>{role}</Tag> : '-'
          }
        />
        <InfoCard label="Active" value={<BooleanIcons value={is_active} />} />
        <InfoCard
          label="Has Password"
          value={<BooleanIcons value={has_password} />}
        />
        <InfoCard
          label="2FA"
          value={<BooleanIcons value={two_factor_enabled} />}
        />
      </div>

      <div className="bg-gray-50 -mx-6 -mb-6 p-6 rounded-b-lg mt-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoCard label="Created At" value={formatDateTime(created_at)} />
          <InfoCard label="Updated At" value={formatDateTime(updated_at)} />
        </div>
      </div>

      <ResetPasswordModal
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        staffAccountId={id}
        email={email}
        apiCall={apiCall}
      />
    </div>
  );
};

export default ItemDetail;
